/**
 * SimplifiedPreDeparturePhase - Streamlined version for workboats
 */

import React, { useState, useEffect } from 'react';
import { 
  MapPin, Users, CheckCircle, AlertTriangle,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { getVesselChecks, getCheckLabels, getRequiredChecks } from './preDepartureConfig';
import { useVessel } from '../../../../../contexts/VesselContext';
import { getRequiredChecks as getRegulatoryChecks } from '../../../../../config/vesselTypeRequirements';

const SimplifiedPreDeparturePhase = ({ 
  activeVoyage,
  onUpdateVoyage,
  onAddLogEntry,
  onPreDepartureComplete,
  crew = [],
  currentVessel,
  vesselType = 'coastal' // 'inland', 'coastal', 'commercial'
}) => {
  const { getCurrentVessel } = useVessel();
  const vessel = getCurrentVessel() || currentVessel;
  
  // Check daily checks status
  const [dailyChecksStatus, setDailyChecksStatus] = useState(null);
  
  useEffect(() => {
    // Check if vessel requires daily checks
    const operatingArea = vessel?.operatingStatus || 'seagoing';
    const vType = vessel?.type || vessel?.vesselType || vesselType;
    
    // Get regulatory requirements
    const vesselConfig = {
      grossTonnage: vessel?.grossTonnage,
      lengthOverall: vessel?.lengthOverall,
      maxPassengers: vessel?.maxPassengers || vessel?.passengerCapacity,
      category: vessel?.category || '6',
      hasPassengers: vessel?.hasPassengers || vessel?.operations?.includes?.('passenger_operations'),
      internationalVoyages: vessel?.internationalVoyages,
      operatingHours: vessel?.operatingHours
    };
    
    const regulatoryChecks = getRegulatoryChecks(vType, 'preDeparture', operatingArea, vesselConfig);
    
    // Check if daily checks are required
    if (regulatoryChecks.daily && regulatoryChecks.daily.length > 0) {
      // Check localStorage for today's daily checks
      const today = new Date().toISOString().split('T')[0];
      const storageKey = vessel?.id ? `daily_checks_${vessel.id}_${today}` : `daily_checks_${today}`;
      const savedChecks = localStorage.getItem(storageKey);
      
      if (savedChecks) {
        const checks = JSON.parse(savedChecks);
        const allComplete = Object.values(checks).every(c => c?.completed);
        setDailyChecksStatus({ required: true, completed: allComplete, checkCount: regulatoryChecks.daily.length });
      } else {
        setDailyChecksStatus({ required: true, completed: false, checkCount: regulatoryChecks.daily.length });
      }
    } else {
      setDailyChecksStatus({ required: false, completed: true });
    }
  }, [vessel, vesselType]);
  // Expanded states
  const [expanded, setExpanded] = useState({
    voyage: true,
    crew: false,
    safety: false
  });

  // Voyage details (minimal)
  const [voyageDetails, setVoyageDetails] = useState({
    departurePort: activeVoyage?.departurePort || '',
    destinationPort: activeVoyage?.destinationPort || '',
    purpose: activeVoyage?.purpose || 'commercial',
    hasPassengers: activeVoyage?.hasPassengers || false,
    passengerCount: activeVoyage?.passengerCount || 0
  });

  // Selected crew (simplified)
  const [selectedCrew, setSelectedCrew] = useState(
    activeVoyage?.crewList || []
  );

  // Safety checklist - adapts to vessel type
  const [safetyChecks, setSafetyChecks] = useState(() => 
    getVesselChecks(vesselType, currentVessel, voyageDetails)
  );

  // Update checks when vessel type or passenger status changes
  React.useEffect(() => {
    setSafetyChecks(getVesselChecks(vesselType, currentVessel, voyageDetails));
  }, [vesselType, currentVessel, voyageDetails.hasPassengers]);

  const toggleCard = (cardName) => {
    setExpanded(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  };

  // Check if ready to depart
  const canDepart = () => {
    // Inland vessels - minimal requirements
    if (vesselType === 'inland') {
      return voyageDetails.departurePort && 
             voyageDetails.destinationPort;
    }
    
    // Coastal/commercial - basic requirements
    return voyageDetails.departurePort && 
           voyageDetails.destinationPort &&
           selectedCrew.length > 0 &&
           Object.values(safetyChecks).every(check => check === true);
  };

  // Handle crew selection
  const toggleCrewMember = (crewMember) => {
    const isSelected = selectedCrew.some(c => c.id === crewMember.id);
    if (isSelected) {
      setSelectedCrew(selectedCrew.filter(c => c.id !== crewMember.id));
    } else {
      setSelectedCrew([...selectedCrew, crewMember]);
    }
  };

  // Handle pre-departure completion
  const handlePreDepartureComplete = () => {
    if (onUpdateVoyage && activeVoyage) {
      const updatedVoyage = {
        ...activeVoyage,
        ...voyageDetails,
        crewList: selectedCrew,
        safetyChecks: safetyChecks,
        preDepartureComplete: true
      };
      onUpdateVoyage(updatedVoyage);
    }
    
    // Add log entry for pre-departure completion
    if (onAddLogEntry) {
      onAddLogEntry({
        type: 'PRE_DEPARTURE_COMPLETE',
        category: 'safety',
        summary: 'Pre-Departure Checks Complete',
        description: `All pre-departure requirements verified. Ready to depart from ${voyageDetails.departurePort} to ${voyageDetails.destinationPort}`,
        voyageId: activeVoyage?.id,
        timestamp: new Date().toISOString(),
        details: {
          crew: selectedCrew.length,
          passengers: voyageDetails.hasPassengers ? voyageDetails.passengerCount : 0,
          checksCompleted: Object.keys(safetyChecks).filter(k => safetyChecks[k]).length
        }
      });
    }
    
    // Signal completion to parent
    if (onPreDepartureComplete) {
      onPreDepartureComplete();
    }
  };


  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Pre-Departure - Simplified</h3>
          <p className="text-sm text-gray-500 mt-1">
            {vesselType === 'inland' ? 'Minimal checks for inland waterways' : 
             vesselType === 'coastal' ? 'Essential checks for coastal workboats' :
             'Standard pre-departure requirements'}
          </p>
        </div>
        
        <div className="p-5 space-y-4">
          {/* Daily Checks Warning if required but not complete */}
          {dailyChecksStatus?.required && !dailyChecksStatus?.completed && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Daily Checks Required</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Your vessel requires {dailyChecksStatus.checkCount} daily checks per regulations. 
                    Please complete these before departure.
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    Note: You can proceed with pre-departure, but daily checks should be completed when vessel is "in use".
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Voyage Details Card */}
          <div className="rounded-lg border-2 border-gray-200">
            <div 
              onClick={() => toggleCard('voyage')}
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <div>
                    <h4 className="text-sm font-medium">Voyage Details</h4>
                    <p className="text-xs text-gray-500">From, to, and purpose</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {voyageDetails.departurePort && voyageDetails.destinationPort && 
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  }
                  {expanded.voyage ? 
                    <ChevronUp className="h-5 w-5 text-gray-400" /> : 
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  }
                </div>
              </div>
            </div>
            
            {expanded.voyage && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Departure Port *</label>
                    <input
                      type="text"
                      value={voyageDetails.departurePort}
                      onChange={(e) => setVoyageDetails({...voyageDetails, departurePort: e.target.value})}
                      placeholder="e.g. London"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Destination Port *</label>
                    <input
                      type="text"
                      value={voyageDetails.destinationPort}
                      onChange={(e) => setVoyageDetails({...voyageDetails, destinationPort: e.target.value})}
                      placeholder="e.g. Dover"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  
                  {vesselType !== 'inland' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Purpose</label>
                        <select
                          value={voyageDetails.purpose}
                          onChange={(e) => setVoyageDetails({...voyageDetails, purpose: e.target.value})}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="commercial">Commercial</option>
                          <option value="delivery">Delivery</option>
                          <option value="survey">Survey</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="passenger">Passenger Service</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={voyageDetails.hasPassengers}
                            onChange={(e) => {
                              setVoyageDetails({...voyageDetails, hasPassengers: e.target.checked});
                              // Reset safety checks to include/exclude passenger checks
                              setSafetyChecks(getVesselChecks());
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">Carrying passengers</span>
                        </label>
                        
                        {voyageDetails.hasPassengers && (
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-gray-700">Count:</label>
                            <input
                              type="number"
                              value={voyageDetails.passengerCount}
                              onChange={(e) => setVoyageDetails({...voyageDetails, passengerCount: parseInt(e.target.value) || 0})}
                              min="0"
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Crew Selection Card - Not shown for inland waterways */}
          {vesselType !== 'inland' && (
            <div className="rounded-lg border-2 border-gray-200">
              <div 
                onClick={() => toggleCard('crew')}
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-green-600" />
                    <div>
                      <h4 className="text-sm font-medium">Crew on Board</h4>
                      <p className="text-xs text-gray-500">
                        {selectedCrew.length > 0 ? `${selectedCrew.length} selected` : 'Select crew members'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedCrew.length > 0 && 
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    }
                    {expanded.crew ? 
                      <ChevronUp className="h-5 w-5 text-gray-400" /> : 
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    }
                  </div>
                </div>
              </div>
              
              {expanded.crew && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="mt-4 space-y-2">
                    {crew.length > 0 ? (
                      crew.map(member => (
                        <label key={member.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedCrew.some(c => c.id === member.id)}
                            onChange={() => toggleCrewMember(member)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">{member.name}</span>
                          {member.role && <span className="text-xs text-gray-500">({member.role})</span>}
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No crew members available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Safety Checks Card */}
          <div className="rounded-lg border-2 border-gray-200">
            <div 
              onClick={() => toggleCard('safety')}
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  <div>
                    <h4 className="text-sm font-medium">Safety Checks</h4>
                    <p className="text-xs text-gray-500">
                      {Object.values(safetyChecks).filter(v => v).length} of {getRequiredChecks(vesselType, safetyChecks).length} completed
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getRequiredChecks(vesselType, safetyChecks).every(key => safetyChecks[key]) && 
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  }
                  {expanded.safety ? 
                    <ChevronUp className="h-5 w-5 text-gray-400" /> : 
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  }
                </div>
              </div>
            </div>
            
            {expanded.safety && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="mt-4 space-y-2">
                  {/* Dynamic safety checks based on vessel configuration */}
                  {Object.entries(getCheckLabels()).map(([key, label]) => {
                    if (!(key in safetyChecks)) return null;
                    
                    return (
                      <label key={key} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={safetyChecks[key] || false}
                          onChange={(e) => setSafetyChecks({...safetyChecks, [key]: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Complete Pre-Departure Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={handlePreDepartureComplete}
              disabled={!canDepart()}
              className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                canDepart()
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <CheckCircle className="h-5 w-5" />
              Complete Pre-Departure Checks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimplifiedPreDeparturePhase;